/**
 * Integrations (PR9) — API keys for POS / partner integrations.
 *
 * A tenant mints a long-lived API key here, drops it into their point-of-sale
 * (Square / Toast / Clover / a custom app), and that POS calls the public
 * `POST /v1/validations/redeem` endpoint to validate a driver's parking by
 * plate. Same post-pay refund as the in-portal kiosk — just driven from the
 * merchant's own checkout instead of the Parkify tablet.
 *
 * The plaintext token is shown EXACTLY ONCE (create response) and is
 * unrecoverable after — we surface it in a copy-now panel with a clear warning.
 */
import { useEffect, useState, type CSSProperties } from 'react'
import type { TenantSession } from '../lib/session'
import { API_BASE, listApiKeys, createApiKey, revokeApiKey, type ApiKey } from '../lib/api'
import { labelStyle, purpleBtn, ghostBtn, screenTitle } from '../ui'

const API_KEYS_GRID_COLUMNS = 'minmax(160px, 1fr) 170px 130px 120px 90px'


export function Integrations({ session }: { session: TenantSession }) {
  const [keys, setKeys] = useState<ApiKey[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [busy, setBusy] = useState(false)
  const [revealed, setRevealed] = useState<{ key: ApiKey; token: string } | null>(null)

  const load = async () => {
    setLoading(true); setErr('')
    try { setKeys((await listApiKeys(session.token)).keys) }
    catch (e) { setErr(e instanceof Error ? e.message : 'load failed'); console.error('[tenant] api-keys', e) }
    finally { setLoading(false) }
  }
  useEffect(() => { void load() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async () => {
    if (busy) return
    setBusy(true)
    try {
      const res = await createApiKey(session.token, newName.trim() || 'API key')
      setRevealed(res)        // show the plaintext ONCE
      setCreating(false); setNewName('')
      await load()
    } catch (e) {
      alert(`Couldn't create the key: ${e instanceof Error ? e.message : 'error'}`)
    } finally { setBusy(false) }
  }

  const handleRevoke = async (k: ApiKey) => {
    if (!confirm(`Revoke "${k.name}" (${k.prefix}…${k.last4})? Any POS using this key stops validating immediately. This can't be undone.`)) return
    try { await revokeApiKey(session.token, k.id); await load() }
    catch (e) { alert(`Revoke failed: ${e instanceof Error ? e.message : 'error'}`) }
  }

  const fmt = (iso?: string) => {
    if (!iso) return '—'
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) }
    catch { return iso }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <h2 style={screenTitle}>Integrations</h2>
        <span style={{ fontSize: 12, color: 'var(--color-gray-600)' }}>{loading ? 'loading…' : err ? '' : `${keys.length} API key${keys.length === 1 ? '' : 's'}`}</span>
        <div style={{ flex: 1 }} />
        <button onClick={() => { setCreating(true); setNewName('') }} style={purpleBtn}>+ New API key</button>
      </div>

      <ExplainerCard />

      <div style={{ background: '#fff', borderRadius: 12, overflowX: 'auto', border: '1px solid var(--color-gray-200)', marginTop: 16 }}>
        <div style={{ display: 'grid', width: '100%', gridTemplateColumns: API_KEYS_GRID_COLUMNS, padding: '10px 16px', borderBottom: '1px solid var(--color-gray-200)', fontSize: 11, fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--color-gray-600)' }}>
          <div>Name</div><div>Key</div><div>Created</div><div>Last used</div><div></div>
        </div>
        {keys.length === 0 ? (
          err ? (
            <div style={{ padding: 16 }}><LoadError message={err} onRetry={() => void load()} /></div>
          ) : (
            <div style={{ padding: 32, textAlign: 'center', fontSize: 13, color: 'var(--color-gray-600)' }}>
              {loading ? '' : 'No API keys yet. Create one to connect your point-of-sale.'}
            </div>
          )
        ) : keys.map(k => (
          <div key={k.id} style={{ display: 'grid', width: '100%', gridTemplateColumns: API_KEYS_GRID_COLUMNS, alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--color-gray-100)', fontSize: 13 }}>
            <div style={{ fontWeight: 600, paddingRight: 8 }}>{k.name}</div>
            <code style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--color-gray-600)' }}>{k.prefix}…{k.last4}</code>
            <div style={{ fontSize: 12, color: 'var(--color-gray-600)' }}>{fmt(k.createdAt)}</div>
            <div style={{ fontSize: 12, color: 'var(--color-gray-600)' }}>{fmt(k.lastUsedAt)}</div>
            <div style={{ textAlign: 'right' }}>
              <button onClick={() => handleRevoke(k)} style={{ ...ghostBtn, fontSize: 11, color: '#b91c1c', borderColor: 'rgba(185,28,28,0.4)', padding: '5px 10px' }}>Revoke</button>
            </div>
          </div>
        ))}
      </div>

      <UsageCard />

      {creating && (
        <div onClick={() => setCreating(false)} style={overlay}>
          <div onClick={e => e.stopPropagation()} style={modal}>
            <h3 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 6px' }}>New API key</h3>
            <p style={{ fontSize: 13, color: 'var(--color-gray-600)', margin: '0 0 16px' }}>Name it for the system you'll connect — e.g. "Toast — Diner on Main".</p>
            <div style={labelStyle}>Key name</div>
            <input
              value={newName} onChange={e => setNewName(e.target.value)} autoFocus
              onKeyDown={e => { if (e.key === 'Enter') void handleCreate() }}
              placeholder="Toast — Diner on Main"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid var(--color-gray-300)', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 18, justifyContent: 'flex-end' }}>
              <button onClick={() => setCreating(false)} style={ghostBtn}>Cancel</button>
              <button onClick={() => void handleCreate()} disabled={busy} style={{ ...purpleBtn, opacity: busy ? 0.6 : 1 }}>{busy ? 'Creating…' : 'Create key'}</button>
            </div>
          </div>
        </div>
      )}

      {revealed && <RevealModal data={revealed} onClose={() => setRevealed(null)} />}
    </div>
  )
}

function ExplainerCard() {
  return (
    <div style={{ background: 'rgba(127,63,152,0.06)', border: '1px solid rgba(127,63,152,0.18)', borderRadius: 12, padding: '16px 18px' }}>
      <div style={{ fontSize: 14.5, fontWeight: 800, letterSpacing: '-0.2px', marginBottom: 4 }}>Validate parking from your point-of-sale</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)', lineHeight: 1.55 }}>
        An API key lets your POS (Square, Toast, Clover, or a custom app) validate a driver's parking by license
        plate — the discount is refunded to the card they already paid with, just like the in-portal kiosk. Create a
        key, paste it into your POS integration, and you're live.
      </div>
    </div>
  )
}

function RevealModal({ data, onClose }: { data: { key: ApiKey; token: string }; onClose: () => void }) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try { await navigator.clipboard.writeText(data.token); setCopied(true); setTimeout(() => setCopied(false), 2000) }
    catch { /* clipboard blocked — the field is selectable as a fallback */ }
  }
  return (
    <div style={overlay}>
      <div onClick={e => e.stopPropagation()} style={modal}>
        <h3 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 6px' }}>Copy your API key now</h3>
        <div style={{ background: 'rgba(241,101,33,0.10)', border: '1px solid rgba(241,101,33,0.4)', borderRadius: 10, padding: '10px 12px', fontSize: 12.5, color: '#b45309', lineHeight: 1.5, marginBottom: 14 }}>
          This is the only time we'll show the full key. Store it somewhere safe — if you lose it, revoke it and create a new one.
        </div>
        <div style={labelStyle}>{data.key.name}</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
          <code style={{ flex: 1, fontFamily: 'monospace', fontSize: 12.5, background: 'var(--color-gray-100)', borderRadius: 8, padding: '10px 12px', wordBreak: 'break-all', userSelect: 'all' }}>{data.token}</code>
          <button onClick={copy} style={{ ...purpleBtn, whiteSpace: 'nowrap' }}>{copied ? 'Copied ✓' : 'Copy'}</button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
          <button onClick={onClose} style={purpleBtn}>Done</button>
        </div>
      </div>
    </div>
  )
}

function UsageCard() {
  const snippet =
    `curl -X POST ${API_BASE}/v1/validations/redeem \\\n` +
    `  -H "X-Parkify-Api-Key: ptk_your_key_here" \\\n` +
    `  -H "Content-Type: application/json" \\\n` +
    `  -d '{"code":"LUNCH2H","plate":"ABC1234"}'`
  return (
    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid var(--color-gray-200)', padding: '18px 20px', marginTop: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Using your key</div>
      <p style={{ fontSize: 13, color: 'var(--color-gray-600)', margin: '0 0 12px', lineHeight: 1.55 }}>
        Send the key as an <code style={{ fontFamily: 'monospace' }}>X-Parkify-Api-Key</code> header (or
        <code style={{ fontFamily: 'monospace' }}> Authorization: Bearer …</code>) to the redeem endpoint with the
        validation <b>code</b> and the driver's <b>plate</b>. Add an <code style={{ fontFamily: 'monospace' }}>Idempotency-Key</code> header
        to make retries safe.
      </p>
      <pre style={{ background: '#231F20', color: '#f5f5f5', borderRadius: 10, padding: '14px 16px', fontSize: 12, overflowX: 'auto', margin: 0, lineHeight: 1.5 }}>{snippet}</pre>
    </div>
  )
}

const overlay: CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(35,31,32,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 16 }
const modal: CSSProperties = { width: 'min(560px, 94vw)', maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }

// Inline load-failure block — shown in place of the empty state when the
// key list fails to load, so a partner never reads "couldn't load" as "no API
// keys" and mints a duplicate. Mirrors the red banner tone used in Validate /
// SignIn; Retry re-runs the loader.
function LoadError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div style={{ background: 'rgba(237,28,36,0.10)', border: '1.5px solid rgba(237,28,36,0.4)', borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 220 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: '#b91c1c', marginBottom: 2 }}>Couldn't load</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)', lineHeight: 1.5 }}>{message}</div>
      </div>
      <button onClick={onRetry} style={{ padding: '8px 18px', borderRadius: 999, background: '#7F3F98', color: '#fff', border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Retry</button>
    </div>
  )
}
